import type { PreviewPatientChoice } from "@/api/preview";

/**
 * What this browser knows about the runs it started: the synthetic patient
 * (so a run that continues one keeps the patient) and the turn a restore runs
 * to (so the Lab can say how far the replay has to go).
 *
 * Session storage only (ADR 0001): the patient ref is never sent to the
 * server for this, put in an address or logged. A run started elsewhere, or
 * after the tab closed, is simply not remembered, and the pickers then ask.
 */

const KEY = "lab.runMemory.v1";
/** Oldest runs are dropped past this many. */
const KEEP = 50;

export type RunMemory = { patientRef?: string; restoreThrough?: number };

function readAll(): Record<string, RunMemory> {
  try {
    const parsed: unknown = JSON.parse(
      window.sessionStorage.getItem(KEY) ?? "{}",
    );
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, RunMemory>)
      : {};
  } catch {
    return {};
  }
}

export function recallRun(previewRunRef: string): RunMemory {
  return readAll()[previewRunRef] ?? {};
}

export function rememberRun(previewRunRef: string, patch: RunMemory): void {
  try {
    const all = readAll();
    const { [previewRunRef]: held, ...rest } = all;
    const next = { ...rest, [previewRunRef]: { ...held, ...patch } };
    const refs = Object.keys(next);
    for (const old of refs.slice(0, Math.max(0, refs.length - KEEP))) {
      delete next[old];
    }
    window.sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Storage refused: the pickers ask instead.
  }
}

/**
 * The patient to keep when the language changes: one of the same gender in the
 * new language. `undefined` means the tester has to choose (no same-gender
 * patient, or the gender is not known).
 */
export function patientForLanguage(
  patients: readonly PreviewPatientChoice[],
  current: PreviewPatientChoice | undefined,
  language: string,
): PreviewPatientChoice | undefined {
  const gender = current?.gender;
  return gender === undefined || gender === null
    ? undefined
    : patients.find(
        (patient) => patient.language === language && patient.gender === gender,
      );
}
